"use client";

import { cn, extractKeyTextValue, isEnrichedProduct } from "@/lib/utils";
import Link from "next/link";
import { EnrichedProduct } from "@/types";
import { Locales } from "@/lib/i18n";
import { CategorieDocument, ProductDocument } from "@/prismicio-types";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import { useProducts } from "@/hooks/useProduct";
import { renderProductCard } from "@/components/molecules/render-product-card";
import { __, getPermalink } from "@/lib/i18n/functions";
import CartProductRecommendationSkeleton from "@/components/blocks/products-loading-skeleton";
import { useRef } from "react";
import { useAddToCart } from "@/hooks/useCartMutations";
import { toast } from "@/components/atoms/toast";
import { useCart } from "@/hooks/useCart";
import { HeartIcon, Loader2, ShoppingCart } from "lucide-react";
import AddToFav from "@/modules/add-to-fav/add-to-fav";
import ModalTemplate from "@/components/molecules/modal-template";
import { useWishlist } from "@/hooks/useWishlist";
import { getCartQuantityStep, resolveProductConditioning } from "@/lib/product-units";

type ArticlesGridProps = {
  headline: string;
  link?: string;
  linkText?: string;
  products: ProductDocument[] | null | EnrichedProduct[];
  className?: string;
  variant?: "default" | "pack";
  locale: Locales;
  collectionCategories: CategorieDocument[][] | null;
};

const ArticlesGrid = ({
  headline,
  link,
  linkText,
  products,
  className,
  variant = "default",
  locale,
  collectionCategories,
}: ArticlesGridProps) => {
  const { cart } = useCart();
  const uids =
    (products &&
      products?.map((product) =>
        isEnrichedProduct(product)
          ? extractKeyTextValue(product.prismic.medusaId)
          : extractKeyTextValue(product.data.medusaId)
      )) ??
    [];
  const { data: medusaProducts, isLoading } = useProducts(locale, uids, uids.length > 0);

  const productValues = Object.values(medusaProducts || {});
  const quantitiesRef = useRef<Record<string, number>>({});

  const { addToCart, isLoading: isAddingAllToCart } = useAddToCart({
    onError: (error) => {
      console.error("Failed to add item to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });
  const { lists, isProductInAnyList } = useWishlist();

  if (isLoading) return <CartProductRecommendationSkeleton itemCount={8} />;

  if (!productValues.at(0) || products?.length === 0) return null;

  const updateProductQuantity = (medusaId: string, quantity: number) => {
    quantitiesRef.current[medusaId] = quantity;
  };

  const isProductsInCart = products?.every((product) => {
    const medusaId = isEnrichedProduct(product)
      ? extractKeyTextValue(product.prismic.medusaId)
      : extractKeyTextValue(product.data.medusaId);
    const medusaProduct = medusaProducts && medusaProducts[medusaId];
    return cart?.items && cart.items.some((item) => item.product_handle === medusaProduct?.handle);
  });

  // TODO : VERIFY WISHLIST LOGIC
  const isProductsInWishlist = uids.every((id) => isProductInAnyList(id));

  const handleAddtoCart = async () => {
    if (!medusaProducts || !products) return;

    try {
      const addToCartPromises = products.map(async (product) => {
        const medusaId = isEnrichedProduct(product)
          ? extractKeyTextValue(product.prismic.medusaId)
          : extractKeyTextValue(product.data.medusaId);
        const medusaProduct = medusaProducts[medusaId];

        if ((medusaProduct?.variants?.length ?? 0) > 1) return;

        if (!medusaProduct?.variants?.[0]?.id) {
          console.warn("FROM ARTICLES GRID : ");
          console.warn(`No variant found for product ${medusaId}`);
          return;
        }
        const conditioning = resolveProductConditioning(
          medusaProduct.metadata?.conditioning,
          medusaProduct.metadata?.conditioning_bac
        );
        const quantity = quantitiesRef.current[medusaId] || getCartQuantityStep(conditioning);
        return addToCart(medusaProduct.variants[0].id, quantity);
      });

      await Promise.all(addToCartPromises);
    } catch (error) {
      console.error("Failed to add items to cart:", error);
      toast.error({
        title: __("Failed to add some products to cart", locale),
      });
    }
  };

  return (
    <section className={cn("container space-y-7.5 px-4 py-6 md:px-7.5 md:py-12", className)}>
      <header className="flex flex-col items-center justify-between gap-3 md:flex-row">
        {headline && <h2 className="font-cabinet-grotesk text-center text-3xl font-medium">{headline}</h2>}
        {linkText && variant === "default" && (
          <Link
            className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
            href={link || "#"}
          >
            {linkText}
          </Link>
        )}
        {variant === "pack" && (
          <div className="flex items-center gap-3.5 max-md:hidden">
            {isProductsInWishlist ? (
              <Link
                href={getPermalink(locale, "account", "wishlist")}
                className={cn(buttonVariants({ variant: "rounded", size: "md" }), "h-11")}
              >
                <HeartIcon />
                {__("View Favorites", locale)}
              </Link>
            ) : (
              <ModalTemplate
                title={__("Please select your list", locale)}
                className="w-full sm:max-w-[506px]"
                classNameTitle="text-lg font-normal"
                triggerAsChild
                modalTrigger={
                  <Button variant="secondary" size="md" className="h-11">
                    {__("Add to wishlist", locale)}
                  </Button>
                }
              >
                <AddToFav multipleProductIds={uids} locale={locale} showForm={!lists?.length} />
              </ModalTemplate>
            )}
            {isProductsInCart ? (
              <Link
                href={getPermalink(locale, "cart")}
                className={cn(buttonVariants({ variant: "primary", size: "md" }), "h-11")}
              >
                <ShoppingCart />
                {__("View Cart", locale)}
              </Link>
            ) : (
              <Button disabled={isAddingAllToCart} onClick={handleAddtoCart} size="md" className="h-11">
                {isAddingAllToCart && <Loader2 className="mr-2 animate-spin" />} {__("Add to cart", locale)}
              </Button>
            )}
          </div>
        )}
      </header>
      {products && products.length >= 1 && (
        <ul
          className={cn(
            "grid grid-cols-2 gap-x-4 gap-y-7.5 md:gap-x-2.5 lg:grid-cols-3",
            products.length >= 4 && "xl:grid-cols-4"
          )}
        >
          {products.map((product, index) => {
            const medusaId = isEnrichedProduct(product)
              ? extractKeyTextValue(product.prismic.medusaId)
              : extractKeyTextValue(product.data.medusaId);
            const medusaProduct = medusaProducts?.[medusaId];
            const category = collectionCategories?.[index]?.[0];

            return (
              <li key={medusaId}>
                {renderProductCard({
                  product,
                  medusaProduct,
                  category,
                  locale,
                  isLoading,
                  className: "max-w-full md:max-w-none",
                  onQuantityChange: (quantity: number) => updateProductQuantity(medusaId, quantity),
                })}
              </li>
            );
          })}
        </ul>
      )}
      {variant === "pack" && (
        <div className="flex flex-col items-center gap-3.5 md:hidden">
          {isProductsInWishlist ? (
            <Link
              href={getPermalink(locale, "account", "wishlist")}
              className={cn(buttonVariants({ variant: "rounded", size: "md" }), "h-11 w-full flex-1")}
            >
              <HeartIcon />
              {__("View Favorites", locale)}
            </Link>
          ) : (
            <ModalTemplate
              title={__("Please select your list", locale)}
              className="w-full sm:max-w-[506px]"
              classNameTitle="text-lg font-normal"
              triggerAsChild
              modalTrigger={
                <Button variant="secondary" size="md" className="h-11 w-full flex-1">
                  {__("Add to wishlist", locale)}
                </Button>
              }
            >
              <AddToFav multipleProductIds={uids} locale={locale} showForm={!lists?.length} />
            </ModalTemplate>
          )}
          {isProductsInCart ? (
            <Link
              href={getPermalink(locale, "cart")}
              className={cn(buttonVariants({ variant: "primary", size: "md" }), "h-11 w-full flex-1")}
            >
              <ShoppingCart />
              {__("View Cart", locale)}
            </Link>
          ) : (
            <Button disabled={isAddingAllToCart} onClick={handleAddtoCart} size="md" className="h-11 w-full flex-1">
              {isAddingAllToCart && <Loader2 className="mr-2 animate-spin" />} {__("Add to cart", locale)}
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

export default ArticlesGrid;
